import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  Award,
  BarChart3,
  Building2,
  Calendar,
  Download,
  Edit3,
  Eye,
  ExternalLink,
  FileCheck2,
  FileWarning,
  FilterX,
  Gavel,
  Loader2,
  Percent,
  PieChart,
  Plus,
  Save,
  Search,
  Target,
  TrendingDown,
  Trophy,
  Trash2,
  Upload,
  X,
  XCircle,
} from 'lucide-react';
import { apiService } from '../services/apiService';
import { Tender, TenderResult, TenderResultCompetitor, TenderResultProofStatus, TenderResultStatus, TenderStatus } from '../types';
import { formatBRLCurrency, parseBRL } from '../utils/money';
import { formatCalendarDate, parseCalendarDate } from '../utils/date';

type OutcomeFilter = 'ALL' | TenderResultStatus;
type ProofFilter = 'ALL' | 'PENDING' | 'ATTACHED' | 'WAIVED' | 'REQUIRED';
type ScopeFilter = 'ALL' | 'PARTICIPATED' | 'TRACKING' | 'NOT_PARTICIPATED';

interface ResultRow {
  tender: Tender;
  result?: TenderResult;
  status: TenderResultStatus;
  proofStatus: TenderResultProofStatus;
  proofRequired: boolean;
  proofMissing: boolean;
  ourBidValue: number;
  winnerBidValue: number;
  category: string;
  segment: string;
  modality: string;
  participated: boolean;
  tracking: boolean;
}

const PARTICIPATED_STATUSES = new Set<TenderStatus | string>([
  TenderStatus.WON,
  TenderStatus.LOST,
  TenderStatus.CLOSED,
  TenderStatus.PARTICIPATE,
  'WON',
  'LOST',
  'CLOSED',
  'PARTICIPATE',
]);

const hasParticipation = (tender: Tender) => Boolean(tender.participationAt)
  || ['PARTICIPATION', 'AUCTION', 'CLOSED'].includes(String(tender.workflowStage || ''))
  || PARTICIPATED_STATUSES.has(tender.status);

const STATUS_LABELS: Record<TenderResultStatus, { label: string; tone: string; icon: React.ElementType }> = {
  WON: { label: 'Ganha', tone: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200', icon: Trophy },
  LOST: { label: 'Perdida', tone: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200', icon: XCircle },
  DISQUALIFIED: { label: 'Desclassificada', tone: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200', icon: FileWarning },
  JUDGING: { label: 'Em julgamento', tone: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200', icon: Gavel },
};

const PROOF_LABELS: Record<TenderResultProofStatus, string> = {
  PENDING: 'Pendente',
  ATTACHED: 'Anexado',
  WAIVED: 'Dispensado',
};

const inferStatus = (tender: Tender, result?: TenderResult): TenderResultStatus => {
  if (result?.resultStatus) return result.resultStatus;
  if (tender.status === TenderStatus.WON || String(tender.status) === 'WON') return 'WON';
  if (tender.status === TenderStatus.LOST || String(tender.status) === 'LOST') return 'LOST';
  return 'JUDGING';
};

const toNumber = (value: unknown) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const parsed = parseBRL(value);
  return parsed || 0;
};

const formatDate = (value?: string | null) => {
  return formatCalendarDate(value, '-');
};

const getYear = (value?: string | null) => {
  if (!value) return '';
  const date = parseCalendarDate(value);
  return date ? String(date.getUTCFullYear()) : '';
};

const csvCell = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const getOutcomeDate = (row: ResultRow) => row.result?.resultDate || row.tender.deadline;

const getProofHref = (value?: string | null) => {
  if (!value) return '';
  if (/^https?:\/\//i.test(value) || value.startsWith('/')) return value;
  return `/api/uploads/${value.split('/').map(encodeURIComponent).join('/')}/view`;
};

const buildRows = (tenders: Tender[], results: TenderResult[]): ResultRow[] => {
  const resultByTender = results.reduce<Record<string, TenderResult>>((acc, result) => {
    acc[result.tenderId] = result;
    return acc;
  }, {});

  return tenders
    .filter((tender) => hasParticipation(tender) || Boolean(tender.trackResult) || Boolean(resultByTender[tender.id]))
    .map((tender) => {
      const result = resultByTender[tender.id];
      const participated = hasParticipation(tender);
      const status = inferStatus(tender, result);
      const proofStatus = result?.proofStatus || 'PENDING';
      const proofRequired = result?.proofRequired ?? participated;
      const proofMissing = proofRequired && proofStatus !== 'ATTACHED';
      return {
        tender,
        result,
        status,
        proofStatus,
        proofRequired,
        proofMissing,
        ourBidValue: toNumber(result?.ourBidValue ?? tender.value),
        winnerBidValue: toNumber(result?.winnerBidValue),
        category: result?.category || (tender as any).category || 'Sem categoria',
        segment: result?.segment || 'Não informado',
        modality: result?.modality || tender.portal || 'Não informada',
        participated,
        tracking: Boolean(tender.trackResult),
      };
    });
};

const ResultadosLicitacoes: React.FC = () => {
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [results, setResults] = useState<TenderResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState<OutcomeFilter>('ALL');
  const [proofFilter, setProofFilter] = useState<ProofFilter>('ALL');
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('ALL');
  const [yearFilter, setYearFilter] = useState('ALL');
  const [segmentFilter, setSegmentFilter] = useState('ALL');
  const [selectedRow, setSelectedRow] = useState<ResultRow | null>(null);
  const [form, setForm] = useState<Partial<TenderResult>>({});
  const proofFileRef = useRef<HTMLInputElement>(null);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [loadedTenders, loadedResults] = await Promise.all([
        apiService.getTenders(),
        apiService.getTenderResults().catch(() => [] as TenderResult[]),
      ]);
      setTenders(loadedTenders);
      setResults(loadedResults);
    } catch (err) {
      console.error('Erro ao carregar resultados:', err);
      setError('Não foi possível carregar o painel de resultados.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const rows = useMemo(() => buildRows(tenders, results), [results, tenders]);

  const availableYears = useMemo(() => {
    return Array.from(new Set(rows.map((row) => getYear(getOutcomeDate(row))).filter(Boolean))).sort((a, b) => Number(b) - Number(a));
  }, [rows]);

  const availableSegments = useMemo(() => {
    return Array.from(new Set(rows.map((row) => row.segment).filter(Boolean))).sort();
  }, [rows]);

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return rows.filter((row) => {
      const matchesTerm =
        !term ||
        row.tender.title.toLowerCase().includes(term) ||
        row.tender.organ.toLowerCase().includes(term) ||
        row.tender.description.toLowerCase().includes(term) ||
        String(row.tender.portal || '').toLowerCase().includes(term) ||
        String(row.tender.state || '').toLowerCase().includes(term) ||
        row.category.toLowerCase().includes(term) ||
        row.modality.toLowerCase().includes(term);
      const matchesScope = scopeFilter === 'ALL'
        || (scopeFilter === 'PARTICIPATED' && row.participated)
        || (scopeFilter === 'TRACKING' && row.tracking)
        || (scopeFilter === 'NOT_PARTICIPATED' && row.tracking && !row.participated);
      const matchesOutcome = outcomeFilter === 'ALL' || row.status === outcomeFilter;
      const matchesProof =
        proofFilter === 'ALL' ||
        row.proofStatus === proofFilter ||
        (proofFilter === 'REQUIRED' && row.proofMissing);
      const matchesYear = yearFilter === 'ALL' || getYear(getOutcomeDate(row)) === yearFilter;
      const matchesSegment = segmentFilter === 'ALL' || row.segment === segmentFilter;
      return matchesTerm && matchesScope && matchesOutcome && matchesProof && matchesYear && matchesSegment;
    });
  }, [outcomeFilter, proofFilter, rows, scopeFilter, searchTerm, segmentFilter, yearFilter]);

  const stats = useMemo(() => {
    const decided = rows.filter((row) => row.status === 'WON' || row.status === 'LOST' || row.status === 'DISQUALIFIED');
    const wins = rows.filter((row) => row.status === 'WON').length;
    const losses = rows.filter((row) => row.status === 'LOST').length;
    const disqualified = rows.filter((row) => row.status === 'DISQUALIFIED').length;
    const judging = rows.filter((row) => row.status === 'JUDGING').length;
    const proofPending = rows.filter((row) => row.proofMissing).length;
    const winRate = decided.length ? (wins / decided.length) * 100 : 0;
    const compared = rows.filter((row) => row.ourBidValue > 0 && row.winnerBidValue > 0);
    const avgGap = compared.length
      ? compared.reduce((sum, row) => sum + ((row.ourBidValue - row.winnerBidValue) / row.winnerBidValue) * 100, 0) / compared.length
      : 0;
    const participated = rows.filter((row) => row.participated).length;
    const tracking = rows.filter((row) => row.tracking).length;
    return { total: rows.length, participated, tracking, wins, losses, disqualified, judging, proofPending, winRate, avgGap };
  }, [rows]);

  const segmentPerformance = useMemo(() => {
    const grouped: Record<string, { total: number; wins: number; value: number }> = filteredRows.reduce((acc, row) => {
      acc[row.segment] = acc[row.segment] || { total: 0, wins: 0, value: 0 };
      acc[row.segment].total += 1;
      acc[row.segment].wins += row.status === 'WON' ? 1 : 0;
      acc[row.segment].value += row.status === 'WON' ? toNumber(row.tender.value) : 0;
      return acc;
    }, {} as Record<string, { total: number; wins: number; value: number }>);
    return Object.entries(grouped)
      .map(([segment, item]) => ({ segment, ...item, rate: item.total ? (item.wins / item.total) * 100 : 0 }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);
  }, [filteredRows]);

  const openEditor = (row: ResultRow) => {
    setSelectedRow(row);
    setForm({
      resultStatus: row.status,
      resultDate: row.result?.resultDate || row.tender.deadline,
      proofRequired: row.proofRequired,
      proofStatus: row.proofStatus,
      proofName: row.result?.proofName || '',
      proofUrl: row.result?.proofUrl || '',
      proofType: row.result?.proofType || '',
      proofMethod: row.result?.proofUrl
        ? (/^https?:\/\//i.test(row.result.proofUrl) ? 'LINK' : 'UPLOAD')
        : row.result?.proofMethod || 'MANUAL',
      proofNotes: row.result?.proofNotes || '',
      ourPlacement: row.result?.ourPlacement || '',
      competitors: row.result?.competitors || [],
      disqualificationReason: row.result?.disqualificationReason || '',
      lossReason: row.result?.lossReason || '',
      ourBidValue: row.result?.ourBidValue ?? row.tender.value,
      winnerBidValue: row.result?.winnerBidValue || '',
      marginPercent: row.result?.marginPercent ?? '',
      winnerName: row.result?.winnerName || '',
      category: row.category === 'Sem categoria' ? '' : row.category,
      segment: row.segment === 'Não informado' ? '' : row.segment,
      modality: row.modality === 'Não informada' ? '' : row.modality,
      executiveNotes: row.result?.executiveNotes || '',
    });
  };

  const updateForm = (field: keyof TenderResult, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateCompetitor = (index: number, field: keyof TenderResultCompetitor, value: string) => {
    setForm((current) => ({
      ...current,
      competitors: (current.competitors || []).map((competitor, competitorIndex) => competitorIndex === index
        ? { ...competitor, [field]: value }
        : competitor),
    }));
  };

  const addCompetitor = () => {
    setForm((current) => ({
      ...current,
      competitors: [...(current.competitors || []), { placement: (current.competitors?.length || 0) + 1, companyName: '', bidValue: '' }],
    }));
  };

  const removeCompetitor = (index: number) => {
    setForm((current) => ({
      ...current,
      competitors: (current.competitors || []).filter((_, competitorIndex) => competitorIndex !== index),
    }));
  };

  const uploadProof = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedRow) return;
    try {
      setIsUploading(true);
      setError(null);
      const attachment = await apiService.uploadTenderAttachmentBase64(selectedRow.tender.id, file);
      setForm((current) => ({
        ...current,
        proofMethod: 'UPLOAD',
        proofStatus: 'ATTACHED',
        proofName: attachment.name || file.name,
        proofUrl: attachment.url,
        proofType: attachment.type || file.type,
      }));
    } catch (err: any) {
      setError(err?.message || 'Erro ao enviar o comprovante.');
    } finally {
      setIsUploading(false);
      if (proofFileRef.current) proofFileRef.current.value = '';
    }
  };

  const saveResult = async () => {
    if (!selectedRow) return;
    try {
      setIsSaving(true);
      const payload = {
        ...form,
        competitors: (form.competitors || []).filter((competitor) => competitor.companyName.trim()),
        resultDate: form.resultDate ? String(form.resultDate).slice(0, 10) : undefined,
        proofStatus: form.proofUrl || form.proofNotes || form.proofName ? 'ATTACHED' : form.proofStatus,
      };
      await apiService.upsertTenderResult(selectedRow.tender.id, payload);
      await loadData();
      setSelectedRow(null);
    } catch (err: any) {
      console.error('Erro ao salvar resultado:', err);
      setError(err?.message || 'Erro ao salvar resultado da licitação.');
    } finally {
      setIsSaving(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setOutcomeFilter('ALL');
    setProofFilter('ALL');
    setScopeFilter('ALL');
    setYearFilter('ALL');
    setSegmentFilter('ALL');
  };

  const exportCsv = () => {
    const headers = [
      'Objeto',
      'Orgao',
      'UF',
      'Acompanhamento',
      'Resultado',
      'Nossa colocação',
      'Data resultado',
      'Categoria',
      'Segmento',
      'Modalidade',
      'Nosso valor',
      'Valor vencedor',
      'Vencedor',
      'Demais concorrentes',
      'Comprovante',
      'Motivo',
    ];
    const rowsCsv = filteredRows.map((row) => [
      row.tender.title,
      row.tender.organ,
      row.tender.state,
      row.participated ? 'Participamos' : 'Acompanhar sem participação',
      STATUS_LABELS[row.status].label,
      row.result?.ourPlacement || '',
      formatDate(getOutcomeDate(row)),
      row.category,
      row.segment,
      row.modality,
      row.ourBidValue.toFixed(2),
      row.winnerBidValue ? row.winnerBidValue.toFixed(2) : '',
      row.result?.winnerName || '',
      (row.result?.competitors || []).map((competitor) => `${competitor.placement}º ${competitor.companyName}`).join(' | '),
      PROOF_LABELS[row.proofStatus],
      row.result?.disqualificationReason || row.result?.lossReason || '',
    ]);
    const content = [headers, ...rowsCsv].map((row) => row.map(csvCell).join(';')).join('\n');
    const blob = new Blob([`\uFEFF${content}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `resultados-licitacoes-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="px-4 sm:px-6 py-5 max-w-[1480px] mx-auto overflow-x-hidden">
      <div className="space-y-5">
        <div className="flex flex-col xl:flex-row xl:items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-brand-50 dark:bg-[rgba(203,106,30,0.15)] px-3 py-1 text-xs font-semibold text-brand-700 dark:text-brand-300">
              <BarChart3 className="w-3.5 h-3.5" />
              Painel de desfechos
            </div>
            <h2 className="mt-3 text-xl font-semibold text-ink-title dark:text-ink-title-dark">Resultados de licitações</h2>
            <p className="mt-1 max-w-4xl text-sm text-ink-subtitle dark:text-ink-subtitle-dark">
              Visão unificada de certames ganhos, perdidos e em julgamento, com comprovantes obrigatórios, motivos de derrota e comparação de preços.
            </p>
          </div>
          <button
            type="button"
            onClick={exportCsv}
            disabled={filteredRows.length === 0}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-line dark:border-line-dark px-3 py-2 text-sm font-medium text-ink-text dark:text-ink-text-dark hover:bg-surface-alt dark:hover:bg-surface-dark-alt disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            Relatório CSV
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-3">
          <KpiCard icon={Target} label="Participações" value={stats.participated} />
          <KpiCard icon={Eye} label="Acompanhando" value={stats.tracking} />
          <KpiCard icon={Trophy} label="Ganhas" value={stats.wins} />
          <KpiCard icon={XCircle} label="Perdidas/desc." value={stats.losses + stats.disqualified} />
          <KpiCard icon={Percent} label="Aproveitamento" value={`${stats.winRate.toFixed(1)}%`} />
          <KpiCard icon={FileWarning} label="Comprovantes pendentes" value={stats.proofPending} danger={stats.proofPending > 0} />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-4">
          <section className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark min-w-0">
            <div className="border-b border-line dark:border-line-dark p-4">
              <div className="flex flex-col xl:flex-row gap-3">
                <div className="relative flex-1 min-w-0">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-placeholder dark:text-ink-placeholder-dark" />
                  <input
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Buscar por edital, objeto, órgão, descrição, portal ou UF..."
                    className="input pl-10"
                  />
                </div>
                <select value={outcomeFilter} onChange={(event) => setOutcomeFilter(event.target.value as OutcomeFilter)} className="input xl:w-44">
                  <option value="ALL">Resultado: Todos</option>
                  <option value="WON">Ganhas</option>
                  <option value="LOST">Perdidas</option>
                  <option value="DISQUALIFIED">Desclassificadas</option>
                  <option value="JUDGING">Em julgamento</option>
                </select>
                <select value={scopeFilter} onChange={(event) => setScopeFilter(event.target.value as ScopeFilter)} className="input xl:w-56">
                  <option value="ALL">Origem: Todas</option>
                  <option value="PARTICIPATED">Participamos</option>
                  <option value="TRACKING">Em acompanhamento</option>
                  <option value="NOT_PARTICIPATED">Sem participação</option>
                </select>
                <select value={proofFilter} onChange={(event) => setProofFilter(event.target.value as ProofFilter)} className="input xl:w-52">
                  <option value="ALL">Comprovante: Todos</option>
                  <option value="REQUIRED">Pendência obrigatória</option>
                  <option value="PENDING">Pendente</option>
                  <option value="ATTACHED">Anexado</option>
                  <option value="WAIVED">Dispensado</option>
                </select>
                <select value={yearFilter} onChange={(event) => setYearFilter(event.target.value)} className="input xl:w-36">
                  <option value="ALL">Ano: Todos</option>
                  {availableYears.map((year) => <option key={year} value={year}>{year}</option>)}
                </select>
                <button type="button" onClick={clearFilters} className="icon-btn xl:w-auto xl:px-3" title="Limpar filtros">
                  <FilterX className="w-4 h-4" />
                  <span className="hidden xl:inline text-sm">Limpar</span>
                </button>
              </div>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center gap-3 py-16 text-ink-subtitle dark:text-ink-subtitle-dark">
                <Loader2 className="w-6 h-6 animate-spin text-brand-600" />
                Carregando resultados...
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                <AlertCircle className="w-10 h-10 text-red-500" />
                <p className="text-sm text-ink-subtitle dark:text-ink-subtitle-dark">{error}</p>
              </div>
            ) : filteredRows.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                <PieChart className="w-10 h-10 text-ink-placeholder dark:text-ink-placeholder-dark" />
                <p className="text-sm font-medium text-ink-title dark:text-ink-title-dark">Nenhum resultado encontrado</p>
                <p className="max-w-md text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                  Registre o desfecho de uma licitação participada para iniciar a análise comparativa.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-line dark:divide-line-dark">
                {filteredRows.map((row) => {
                  const config = STATUS_LABELS[row.status];
                  const Icon = config.icon;
                  const priceGap = row.ourBidValue > 0 && row.winnerBidValue > 0
                    ? ((row.ourBidValue - row.winnerBidValue) / row.winnerBidValue) * 100
                    : null;
                  return (
                    <article key={row.tender.id} className="p-4 hover:bg-surface-alt dark:hover:bg-surface-dark-alt transition-colors">
                      <div className="flex flex-col xl:flex-row xl:items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${config.tone}`}>
                              <Icon className="w-3.5 h-3.5" />
                              {config.label}
                            </span>
                            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                              row.participated
                                ? 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200'
                                : 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200'
                            }`}>
                              {row.participated ? <Target className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                              {row.participated ? 'Participamos' : 'Acompanhar sem participação'}
                            </span>
                            {row.result?.ourPlacement && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                                {row.result.ourPlacement}ª colocação
                              </span>
                            )}
                            {row.proofMissing ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-red-100 dark:bg-red-900/40 px-2.5 py-1 text-xs font-semibold text-red-800 dark:text-red-200">
                                <FileWarning className="w-3.5 h-3.5" />
                                Comprovante obrigatório pendente
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 dark:bg-green-900/40 px-2.5 py-1 text-xs font-semibold text-green-800 dark:text-green-200">
                                <FileCheck2 className="w-3.5 h-3.5" />
                                {PROOF_LABELS[row.proofStatus]}
                              </span>
                            )}
                          </div>
                          <h3 className="text-sm font-semibold text-ink-title dark:text-ink-title-dark leading-snug">{row.tender.title}</h3>
                          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                            <span className="inline-flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />{row.tender.organ}</span>
                            <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{formatDate(getOutcomeDate(row))}</span>
                            <span>{row.category}</span>
                            <span>{row.modality}</span>
                          </div>
                          {(row.result?.lossReason || row.result?.disqualificationReason) && (
                            <p className="mt-2 text-xs text-ink-subtitle dark:text-ink-subtitle-dark line-clamp-2">
                              {row.result?.disqualificationReason || row.result?.lossReason}
                            </p>
                          )}
                          {(row.result?.competitors || []).length > 0 && (
                            <p className="mt-2 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                              <span className="font-semibold text-ink-title dark:text-ink-title-dark">Classificação:</span>{' '}
                              {(row.result?.competitors || []).map((competitor) => `${competitor.placement}º ${competitor.companyName}`).join(' · ')}
                            </p>
                          )}
                          {row.result?.proofUrl && (
                            <a href={getProofHref(row.result.proofUrl)} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brand-700 dark:text-brand-300 hover:underline">
                              <ExternalLink className="w-3.5 h-3.5" />Abrir comprovante
                            </a>
                          )}
                        </div>
                        <div className="xl:w-72 flex-shrink-0 grid grid-cols-2 gap-2 text-xs">
                          <MetricBox label="Nosso preço" value={row.ourBidValue ? formatBRLCurrency(row.ourBidValue) : '-'} />
                          <MetricBox label="Preço vencedor" value={row.winnerBidValue ? formatBRLCurrency(row.winnerBidValue) : '-'} />
                          <MetricBox label="Diferença" value={priceGap === null ? '-' : `${priceGap.toFixed(1)}%`} />
                          <button
                            type="button"
                            onClick={() => openEditor(row)}
                            className="inline-flex items-center justify-center gap-2 rounded-md border border-line dark:border-line-dark px-3 py-2 text-sm text-ink-text dark:text-ink-text-dark hover:bg-surface dark:hover:bg-surface-dark"
                          >
                            <Edit3 className="w-4 h-4" />
                            Editar
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          <aside className="space-y-4 min-w-0">
            <section className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark p-4">
              <div className="flex items-center gap-2 mb-3">
                <TrendingDown className="w-4 h-4 text-brand-700 dark:text-brand-300" />
                <h3 className="text-sm font-semibold text-ink-title dark:text-ink-title-dark">Comparativo de preços</h3>
              </div>
              <div className="text-2xl font-semibold text-ink-title dark:text-ink-title-dark">
                {stats.avgGap.toFixed(1)}%
              </div>
              <p className="mt-1 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                Diferença média entre preço ofertado e preço vencedor nos itens com valores registrados.
              </p>
            </section>

            <section className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark p-4">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-brand-700 dark:text-brand-300" />
                  <h3 className="text-sm font-semibold text-ink-title dark:text-ink-title-dark">Desempenho por segmento</h3>
                </div>
                <select value={segmentFilter} onChange={(event) => setSegmentFilter(event.target.value)} className="input max-w-[150px]">
                  <option value="ALL">Todos</option>
                  {availableSegments.map((segment) => <option key={segment} value={segment}>{segment}</option>)}
                </select>
              </div>
              <div className="space-y-3">
                {segmentPerformance.length === 0 ? (
                  <p className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">Sem dados suficientes.</p>
                ) : segmentPerformance.map((item) => (
                  <div key={item.segment}>
                    <div className="flex items-center justify-between gap-3 text-xs">
                      <span className="font-medium text-ink-title dark:text-ink-title-dark truncate">{item.segment}</span>
                      <span className="text-ink-subtitle dark:text-ink-subtitle-dark">{item.rate.toFixed(0)}%</span>
                    </div>
                    <div className="mt-1 h-2 rounded-full bg-surface-alt dark:bg-surface-dark-alt overflow-hidden">
                      <div className="h-full rounded-full bg-brand-600" style={{ width: `${Math.min(100, item.rate)}%` }} />
                    </div>
                    <div className="mt-1 text-[11px] text-ink-subtitle dark:text-ink-subtitle-dark">
                      {item.wins}/{item.total} ganho(s) - {formatBRLCurrency(item.value)}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </aside>
        </div>
      </div>

      {selectedRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6">
          <div className="w-full max-w-4xl max-h-[90vh] overflow-auto rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark shadow-xl">
            <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-line dark:border-line-dark bg-surface dark:bg-surface-dark p-4">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-ink-title dark:text-ink-title-dark">Registrar resultado</h3>
                <p className="mt-1 text-xs text-ink-subtitle dark:text-ink-subtitle-dark truncate">{selectedRow.tender.title}</p>
              </div>
              <button type="button" onClick={() => setSelectedRow(null)} className="icon-btn" title="Fechar">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Field label="Resultado">
                <select value={form.resultStatus || 'JUDGING'} onChange={(event) => updateForm('resultStatus', event.target.value)} className="input">
                  <option value="JUDGING">Em julgamento</option>
                  <option value="WON">Ganha</option>
                  <option value="LOST">Perdida</option>
                  <option value="DISQUALIFIED">Desclassificada</option>
                </select>
              </Field>
              <Field label="Data do resultado">
                <input type="date" value={String(form.resultDate || '').slice(0, 10)} onChange={(event) => updateForm('resultDate', event.target.value)} className="input" />
              </Field>
              <Field label="Categoria">
                <input value={String(form.category || '')} onChange={(event) => updateForm('category', event.target.value)} className="input" placeholder="Controle de ponto, CFTV, acesso..." />
              </Field>
              <Field label="Segmento">
                <input value={String(form.segment || '')} onChange={(event) => updateForm('segment', event.target.value)} className="input" placeholder="Software, hardware, serviços..." />
              </Field>
              <Field label="Modalidade">
                <input value={String(form.modality || '')} onChange={(event) => updateForm('modality', event.target.value)} className="input" placeholder="Pregão eletrônico, dispensa..." />
              </Field>
              <Field label="Empresa vencedora">
                <input value={String(form.winnerName || '')} onChange={(event) => updateForm('winnerName', event.target.value)} className="input" placeholder="Nome do vencedor, quando houver" />
              </Field>
              <Field label="Nossa colocação">
                <input type="number" min="1" step="1" value={String(form.ourPlacement || '')} onChange={(event) => updateForm('ourPlacement', event.target.value)} className="input" placeholder="Ex.: 1, 2, 3..." />
              </Field>
              <Field label="Nosso preço ofertado">
                <input value={String(form.ourBidValue || '')} onChange={(event) => updateForm('ourBidValue', event.target.value)} className="input" placeholder="R$ 0,00" />
              </Field>
              <Field label="Preço vencedor">
                <input value={String(form.winnerBidValue || '')} onChange={(event) => updateForm('winnerBidValue', event.target.value)} className="input" placeholder="R$ 0,00" />
              </Field>
              <Field label="Margem comercial estimada (%)">
                <input type="number" min="-99" max="1000" step="0.01" value={String(form.marginPercent ?? '')} onChange={(event) => updateForm('marginPercent', event.target.value)} className="input" placeholder="Ex.: 18,50" />
              </Field>
              <div className="lg:col-span-2 border-t border-line dark:border-line-dark pt-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <h4 className="text-sm font-semibold text-ink-title dark:text-ink-title-dark">Classificação dos concorrentes</h4>
                    <p className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">Registre os concorrentes classificados, seus valores e observações.</p>
                  </div>
                  <button type="button" onClick={addCompetitor} className="btn-secondary inline-flex items-center gap-2 text-xs">
                    <Plus className="w-4 h-4" />Adicionar concorrente
                  </button>
                </div>
                <div className="space-y-2">
                  {(form.competitors || []).map((competitor, index) => (
                    <div key={competitor.id || index} className="grid grid-cols-1 sm:grid-cols-[76px_minmax(0,1fr)_minmax(130px,180px)_40px] gap-2 items-center">
                      <input type="number" min="1" step="1" aria-label={`Colocação do concorrente ${index + 1}`} value={String(competitor.placement || '')} onChange={(event) => updateCompetitor(index, 'placement', event.target.value)} className="input" placeholder="Pos." />
                      <input aria-label={`Nome do concorrente ${index + 1}`} value={competitor.companyName} onChange={(event) => updateCompetitor(index, 'companyName', event.target.value)} className="input" placeholder="Empresa concorrente" />
                      <input aria-label={`Valor do concorrente ${index + 1}`} value={String(competitor.bidValue || '')} onChange={(event) => updateCompetitor(index, 'bidValue', event.target.value)} className="input" placeholder="Valor ofertado" />
                      <button type="button" onClick={() => removeCompetitor(index)} className="icon-btn" title="Remover concorrente">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {(form.competitors || []).length === 0 && (
                    <p className="rounded-md border border-dashed border-line dark:border-line-dark p-3 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">Nenhum concorrente informado.</p>
                  )}
                </div>
              </div>
              <Field label="Status do comprovante">
                <select value={form.proofStatus || 'PENDING'} onChange={(event) => updateForm('proofStatus', event.target.value)} className="input">
                  <option value="PENDING">Pendente</option>
                  <option value="ATTACHED">Anexado</option>
                  <option value="WAIVED">Dispensado</option>
                </select>
              </Field>
              <label className="flex items-center gap-2 rounded-md border border-line dark:border-line-dark px-3 py-2 text-sm text-ink-text dark:text-ink-text-dark">
                <input
                  type="checkbox"
                  checked={form.proofRequired !== false}
                  onChange={(event) => updateForm('proofRequired', event.target.checked)}
                />
                Comprovante obrigatório
              </label>
              <Field label="Forma do comprovante">
                <select value={form.proofMethod || 'MANUAL'} onChange={(event) => updateForm('proofMethod', event.target.value)} className="input">
                  <option value="MANUAL">Registro manual</option>
                  <option value="LINK">Link externo</option>
                  <option value="UPLOAD">Anexar arquivo ou print</option>
                </select>
              </Field>
              <Field label="Nome do comprovante">
                <input value={String(form.proofName || '')} onChange={(event) => updateForm('proofName', event.target.value)} className="input" placeholder="Ata da sessão, publicação no DO..." />
              </Field>
              {form.proofMethod === 'LINK' && (
                <Field label="Link do comprovante" wide>
                  <input value={String(form.proofUrl || '')} onChange={(event) => updateForm('proofUrl', event.target.value)} className="input" placeholder="https://portal.exemplo/resultado" />
                </Field>
              )}
              {form.proofMethod === 'UPLOAD' && (
                <div className="lg:col-span-2 rounded-md border border-line dark:border-line-dark p-3">
                  <input ref={proofFileRef} type="file" accept="application/pdf,image/png,image/jpeg,.doc,.docx,.xls,.xlsx" className="hidden" onChange={uploadProof} />
                  <div className="flex flex-wrap items-center gap-2">
                    <button type="button" disabled={isUploading} onClick={() => proofFileRef.current?.click()} className="btn-secondary inline-flex items-center gap-2">
                      {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {isUploading ? 'Enviando...' : 'Selecionar print ou arquivo'}
                    </button>
                    {form.proofName && <span className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">{form.proofName}</span>}
                    {form.proofUrl && (
                      <a href={getProofHref(form.proofUrl)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 dark:text-brand-300 hover:underline">
                        <ExternalLink className="w-3.5 h-3.5" />Abrir comprovante
                      </a>
                    )}
                  </div>
                </div>
              )}
              {form.proofMethod === 'MANUAL' && (
                <Field label="Registro manual do comprovante" wide>
                  <textarea value={String(form.proofNotes || '')} onChange={(event) => updateForm('proofNotes', event.target.value)} className="input min-h-[84px]" placeholder="Informe onde e como o resultado foi confirmado, data, responsável e demais evidências." />
                </Field>
              )}
              <Field label="Motivo da derrota" wide>
                <textarea value={String(form.lossReason || '')} onChange={(event) => updateForm('lossReason', event.target.value)} className="input min-h-[92px]" placeholder="Preço, documentação, especificação, prazo, estratégia comercial..." />
              </Field>
              <Field label="Motivo da desclassificação" wide>
                <textarea value={String(form.disqualificationReason || '')} onChange={(event) => updateForm('disqualificationReason', event.target.value)} className="input min-h-[92px]" placeholder="Registrar exigência, item do edital e ação corretiva." />
              </Field>
              <Field label="Notas executivas" wide>
                <textarea value={String(form.executiveNotes || '')} onChange={(event) => updateForm('executiveNotes', event.target.value)} className="input min-h-[110px]" placeholder="Aprendizados, riscos, próximos ajustes comerciais e técnicos." />
              </Field>
            </div>

            <div className="sticky bottom-0 flex justify-end gap-2 border-t border-line dark:border-line-dark bg-surface dark:bg-surface-dark p-4">
              <button type="button" onClick={() => setSelectedRow(null)} className="btn-secondary">Cancelar</button>
              <button type="button" onClick={saveResult} disabled={isSaving} className="btn-primary inline-flex items-center gap-2">
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Salvar resultado
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const KpiCard = ({ icon: Icon, label, value, danger = false }: { icon: React.ElementType; label: string; value: React.ReactNode; danger?: boolean }) => (
  <div className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark p-4">
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs font-semibold uppercase tracking-wider text-ink-subtitle dark:text-ink-subtitle-dark">{label}</span>
      <Icon className={`w-4 h-4 ${danger ? 'text-red-600' : 'text-brand-700 dark:text-brand-300'}`} />
    </div>
    <div className="mt-2 text-2xl font-semibold text-ink-title dark:text-ink-title-dark">{value}</div>
  </div>
);

const MetricBox = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="rounded-md border border-line dark:border-line-dark bg-surface dark:bg-surface-dark p-2">
    <div className="text-[11px] text-ink-subtitle dark:text-ink-subtitle-dark">{label}</div>
    <div className="mt-1 font-semibold text-ink-title dark:text-ink-title-dark truncate">{value}</div>
  </div>
);

const Field = ({ label, children, wide = false }: { label: string; children: React.ReactNode; wide?: boolean }) => (
  <label className={`block ${wide ? 'lg:col-span-2' : ''}`}>
    <span className="mb-1 block text-xs font-semibold text-ink-label dark:text-ink-label-dark">{label}</span>
    {children}
  </label>
);

export default ResultadosLicitacoes;
